import type { ChangeEvent, ComponentPropsWithoutRef, JSX, ReactNode } from 'react';
import { forwardRef } from 'react';
import { RadioCardRoot } from './RadioCardRoot';
import { RadioCardDefaultLayout } from './RadioCardDefaultLayout';
import type { RadioCardDefaultLayoutProps } from './RadioCardDefaultLayout';
import { RadioCardChildrenContainer } from './RadioCardChildrenContainer';

type BaseRadioCardProps = Omit<
  ComponentPropsWithoutRef<'input'>,
  'checked' | 'disabled' | 'id' | 'name' | 'onChange' | 'value'
> & {
  /**
   * Indicates the state of the radio
   */
  checked?: boolean;
  /**
   * Disables the card
   */
  disabled?: boolean;
  /**
   * Provides an ID for the input
   */
  id?: string;
  /**
   * The name attribute of the input
   */
  name?: string;
  /**
   * Callback function invoked when the value changes
   */
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  /**
   * The value attribute of the input
   */
  value: string;
};

type RadioCardWithChildren = BaseRadioCardProps & {
  /**
   * Custom content to display inside the card.
   */
  children: ReactNode;
  icon?: never;
  label?: never;
  description?: never;
  showIndicator?: never;
  isGated?: never;
};

type RadioCardWithDefaultLayout = BaseRadioCardProps &
  RadioCardDefaultLayoutProps & {
    children?: never;
  };

export type RadioCardProps = RadioCardWithChildren | RadioCardWithDefaultLayout;

export const RadioCard = forwardRef<HTMLInputElement, RadioCardProps>(
  (
    { icon, label, description, showIndicator, isGated, children, ...rootProps },
    ref,
  ): JSX.Element => {
    return (
      <RadioCardRoot
        ref={ref}
        isGated={isGated}
        padding={children != null ? 'space-00' : 'space-04'}
        {...rootProps}
      >
        {children != null ? (
          <RadioCardChildrenContainer data-wui-radio-card-image="cover">
            {children}
          </RadioCardChildrenContainer>
        ) : (
          <RadioCardDefaultLayout
            description={description}
            icon={icon}
            isGated={isGated}
            label={label}
            showIndicator={showIndicator}
          />
        )}
      </RadioCardRoot>
    );
  },
);

RadioCard.displayName = 'RadioCard';
